import ErrorFallback from '@/components/ErrorFallback';
import { FirstRunGuard } from '@/components/FirstRunGuard';
import { PiiReminderDialog } from '@/components/PiiReminderDialog';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ACCOUNTS_ADMIN_PERMISSION, AuthProvider, ORG_ADMIN_PERMISSION } from '@/contexts/AuthContext';
import { RecordingProvider } from '@/contexts/RecordingContext';
import { MemoryPage } from '@/features/user-memory/components/MemoryPage';
import { Sentry } from '@/lib/sentry';
import Admin from '@/pages/admin';
import ClinicSelect from '@/pages/clinic-select';
import ClinicSettings from '@/pages/clinic-settings';
import Conversation from '@/pages/conversation';
import Dashboard from '@/pages/dashboard';
import Dictation from '@/pages/dictation';
import HelpCenter from '@/pages/help-center';
// EmbedDictation has its own entry point (embed-main.tsx / embed.html)
import Login from '@/pages/login';
import ModelReview from '@/pages/model-review';
import NotFound from '@/pages/not-found';
import Onboarding from '@/pages/onboarding';
import Patients from '@/pages/patients';
import PiiTraining from '@/pages/pii-training';
import Session from '@/pages/session';
import Settings from '@/pages/settings';
import SummaryQuality from '@/pages/summary-quality';
import Tasks from '@/pages/tasks';
import Templates from '@/pages/templates';
import TemplatesOrg from '@/pages/templates-org';
import TemplatesOrgDetail from '@/pages/templates-org-detail';
import TemplatesSystem from '@/pages/templates-system';
import TemplatesUserDetail from '@/pages/templates-user-detail';
import Terms from '@/pages/terms';
import TestSuite from '@/pages/test-suite';
import UsageCosts from '@/pages/usage-costs';
import { QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react';
import { Route, Switch } from 'wouter';
import { queryClient } from './lib/queryClient';

const NewVideoVisit = lazy(() => import('@/pages/video-visit').then((module) => ({ default: module.NewVideoVisit })));
const VideoList = lazy(() => import('@/pages/video-list').then((module) => ({ default: module.VideoList })));
const VideoVisit = lazy(() => import('@/pages/video-visit').then((module) => ({ default: module.VideoVisit })));

function Router() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      {/* Invite acceptance reuses the Login page (reads ?token=&org= from the URL). */}
      <Route path="/join" component={Login} />
      <Route path="/clinic/select" component={ClinicSelect} />
      <Route path="/terms" component={Terms} />
      <Route path="/onboarding">
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      </Route>
      <Route path="/pii-training">
        <ProtectedRoute>
          <PiiTraining />
        </ProtectedRoute>
      </Route>
      <Route path="/">
        <ProtectedRoute>
          <FirstRunGuard>
            <Dashboard />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/session/new">
        <ProtectedRoute>
          <FirstRunGuard>
            <Session />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/dictation/new">
        <ProtectedRoute>
          <FirstRunGuard>
            <Dictation />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/dictation/:sessionId">
        {(params) => (
          <ProtectedRoute>
            <FirstRunGuard>
              <Dictation sessionId={params.sessionId} />
            </FirstRunGuard>
          </ProtectedRoute>
        )}
      </Route>
      {/* Legacy route for old session URLs */}
      <Route path="/session/:sessionId">
        {(params) => (
          <ProtectedRoute>
            <FirstRunGuard>
              <Dictation sessionId={params.sessionId} />
            </FirstRunGuard>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/conversation/new">
        <ProtectedRoute>
          <FirstRunGuard>
            <Conversation />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/conversation/:sessionId">
        {(params) => (
          <ProtectedRoute>
            <FirstRunGuard>
              <Conversation sessionId={params.sessionId} />
            </FirstRunGuard>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/tasks">
        <ProtectedRoute>
          <FirstRunGuard>
            <Tasks />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/video">
        <ProtectedRoute>
          <Suspense fallback={null}>
            <VideoList />
          </Suspense>
        </ProtectedRoute>
      </Route>
      <Route path="/video/new">
        <ProtectedRoute>
          <Suspense fallback={null}>
            <NewVideoVisit />
          </Suspense>
        </ProtectedRoute>
      </Route>
      <Route path="/video/:visitId">
        {(params) => (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <VideoVisit visitId={params.visitId} />
            </Suspense>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/patients">
        <ProtectedRoute>
          <FirstRunGuard>
            <Patients />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/templates">
        <ProtectedRoute>
          <FirstRunGuard>
            <Templates />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/templates/org">
        <ProtectedRoute requiredPermissions={['org:admin']}>
          <FirstRunGuard>
            <TemplatesOrg />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/templates/orgs/:orgId">
        {(params) => (
          <ProtectedRoute requireAdmin>
            <FirstRunGuard>
              <TemplatesOrgDetail orgId={params.orgId} />
            </FirstRunGuard>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/templates/system">
        <ProtectedRoute requireAdmin>
          <FirstRunGuard>
            <TemplatesSystem />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/templates/users/:accountId">
        {(params) => (
          <ProtectedRoute requireAdmin>
            <FirstRunGuard>
              <TemplatesUserDetail accountId={params.accountId} />
            </FirstRunGuard>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/faqs/:section">
        {(params) => (
          <ProtectedRoute>
            <FirstRunGuard>
              <HelpCenter section={params.section} />
            </FirstRunGuard>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/faq/:section">
        {(params) => (
          <ProtectedRoute>
            <FirstRunGuard>
              <HelpCenter section={params.section} />
            </FirstRunGuard>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/faqs">
        <ProtectedRoute>
          <FirstRunGuard>
            <HelpCenter />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/faq">
        <ProtectedRoute>
          <FirstRunGuard>
            <HelpCenter />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/settings">
        <ProtectedRoute>
          <FirstRunGuard>
            <Settings />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/settings/memory">
        <ProtectedRoute>
          <FirstRunGuard>
            <MemoryPage />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/clinic-settings">
        <ProtectedRoute requiredPermissions={[ORG_ADMIN_PERMISSION, ACCOUNTS_ADMIN_PERMISSION]}>
          <FirstRunGuard>
            <ClinicSettings />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/admin">
        <ProtectedRoute requireAdmin>
          <FirstRunGuard>
            <Admin />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/usage-costs">
        <ProtectedRoute requireAdmin>
          <FirstRunGuard>
            <UsageCosts />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/model-review">
        <ProtectedRoute requireAdmin>
          <FirstRunGuard>
            <ModelReview />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/summary-quality">
        <ProtectedRoute requireAdmin>
          <FirstRunGuard>
            <SummaryQuality />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/test-suite">
        <ProtectedRoute requireAdmin>
          <FirstRunGuard>
            <TestSuite />
          </FirstRunGuard>
        </ProtectedRoute>
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RecordingProvider>
            <TooltipProvider>
              <Router />
              <Toaster />
              <PiiReminderDialog />
            </TooltipProvider>
          </RecordingProvider>
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
